/**
 * Enquiry service — wired to the REAL backend. Keeps the reference UI's validation + honeypot seam,
 * but `submitEnquiry` POSTs to the live public inquiry API (`/public/inquiries`) with the enquiry
 * `type` (PRODUCT / RETAILER / DISTRIBUTOR / GENERAL) added in Phase 0, so retailer/distributor/contact
 * leads flow through the SAME pipeline as product inquiries and land in the dashboard. Nothing else in
 * the UI changed — this is the single integration seam the reference UI was designed around.
 */
import { request } from '../../api/client';
import { productRepository } from './productRepository';

export type EnquiryType = 'product' | 'farmer' | 'retailer' | 'distributor' | 'general';

export interface EnquiryPayload {
  type: EnquiryType;
  name: string;
  email?: string;
  phone: string;
  location?: string;
  productSlug?: string;
  message: string;
  /** honeypot — must be empty */
  company?: string;
}

export interface EnquiryResult {
  ok: true;
  reference: string;
}

export class EnquiryValidationError extends Error {
  fields: Record<string, string>;
  constructor(fields: Record<string, string>) {
    super('Please correct the highlighted fields.');
    this.name = 'EnquiryValidationError';
    this.fields = fields;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-\s\d]{7,20}$/;

export function validateEnquiry(payload: Partial<EnquiryPayload>): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!payload.name || payload.name.trim().length < 2) errors.name = 'Please enter your name.';
  if (!payload.phone || !PHONE_RE.test(payload.phone)) errors.phone = 'Please enter a valid phone number.';
  if (payload.email && !EMAIL_RE.test(payload.email)) errors.email = 'Please enter a valid email address.';
  if (!payload.message || payload.message.trim().length < 10)
    errors.message = 'Please add a few words about your enquiry (min. 10 characters).';
  return errors;
}

/** Map the UI enquiry type to the backend triage type. */
function backendType(t: EnquiryType): 'PRODUCT' | 'RETAILER' | 'DISTRIBUTOR' | 'GENERAL' {
  if (t === 'product') return 'PRODUCT';
  if (t === 'retailer') return 'RETAILER';
  if (t === 'distributor') return 'DISTRIBUTOR';
  return 'GENERAL'; // general + farmer
}

export async function submitEnquiry(payload: EnquiryPayload): Promise<EnquiryResult> {
  // Honeypot: a bot filled the hidden field → silently accept-and-drop (the backend also honeypots).
  if (payload.company && payload.company.trim() !== '') {
    return { ok: true, reference: 'GB-000000' };
  }

  const errors = validateEnquiry(payload);
  if (Object.keys(errors).length > 0) throw new EnquiryValidationError(errors);

  const product = payload.productSlug ? productRepository.getProductBySlugSync(payload.productSlug) : undefined;
  const messageParts = [payload.message.trim()];
  if (payload.type === 'farmer') messageParts.unshift('[Farmer enquiry]');
  if (payload.location?.trim()) messageParts.push(`Location: ${payload.location.trim()}`);

  const res = await request<{ inquiryNo: string; publicToken: string; status: string }>({
    url: '/public/inquiries',
    method: 'POST',
    data: {
      type: backendType(payload.type),
      customerName: payload.name.trim(),
      customerEmail: payload.email?.trim() || undefined,
      customerPhone: payload.phone.trim(),
      productNameText: product?.name || undefined,
      city: payload.location?.trim() || undefined,
      message: messageParts.join(' — '),
      website: payload.company || undefined, // honeypot passthrough
    },
  });

  return { ok: true, reference: res.inquiryNo };
}
